import { useMemo } from 'react';

import { useGenericSearch } from '@/hooks/useGenericSearch';
import { MonitoringCounts, Statistic } from '@/types';
import { QueryStatus, useMergeStatus } from '@/utils/api';

/**
 * Maps cloud resource surface types to cloud provider names
 * e.g., "AWS::EC2::Instance" -> "amazon"
 */
function mapSurfaceToProvider(surface: string): string {
  if (surface.startsWith('AWS::')) return 'amazon';
  if (surface.startsWith('Azure::') || surface.startsWith('Microsoft.'))
    return 'azure';
  if (surface.startsWith('GCP::') || surface.startsWith('google.'))
    return 'gcp';
  if (surface.startsWith('DigitalOcean::')) return 'digitalocean';
  // Return original value if no mapping found (for class-based values like "ipv4", "domain", etc.)
  return surface;
}

function getStatsObject(stats: Statistic[]) {
  return stats.reduce(
    (acc, curr) => {
      // Map cloud resource surfaces to provider names
      const key = mapSurfaceToProvider(curr.value);
      const count = (curr.data?.count as number) || 0;

      // Accumulate counts for the same provider (in case of multiple surface types)
      return {
        ...acc,
        [key]: ((acc[key] as number) || 0) + count,
      };
    },
    {} as Record<string, number>
  );
}

export function useGetMonitoringCounts(options?: { email?: string }): {
  data: MonitoringCounts;
  status: QueryStatus;
  error: Error | null;
  refetch: () => void;
} {
  const cacheOptions = {
    staleTime: 30000, // 30 seconds - data stays fresh
    gcTime: 300000, // 5 minutes - keep in cache
    refetchOnMount: false, // Only refetch if stale
    refetchOnWindowFocus: false, // Prevent focus refetch
    email: options?.email,
  };

  const {
    data: externalAssetCountsGenericSearch,
    status: externalAssetCountsGenericSearchStatus,
    error: externalError,
    refetch: refetchExternal,
  } = useGenericSearch(
    {
      query: '#statistic#monitoring_count#externalasset#now#',
    },
    cacheOptions
  );
  const {
    data: internalAssetCountsGenericSearch,
    status: internalAssetCountsGenericSearchStatus,
    error: internalError,
    refetch: refetchInternal,
  } = useGenericSearch(
    {
      query: '#statistic#monitoring_count#internalasset#now#',
    },
    cacheOptions
  );
  const {
    data: assetCountsByType,
    status: assetCountsByTypeStatus,
    error: assetTypeError,
    refetch: refetchAssetType,
  } = useGenericSearch(
    {
      query: '#statistic#type#type#now#',
    },
    cacheOptions
  );
  const {
    data: webapps,
    status: webappsStatus,
    error: webappsError,
    refetch: refetchWebapps,
  } = useGenericSearch(
    {
      query: '#statistic#webapps#webapps#now#',
    },
    cacheOptions
  );

  const externalAssetCountsGenericSearchStats =
    externalAssetCountsGenericSearch?.statistics;
  const internalAssetCountsGenericSearchStats =
    internalAssetCountsGenericSearch?.statistics;
  const assetCountsByTypeStats = assetCountsByType?.statistics;
  const webappsStats = webapps?.statistics;

  const counts = useMemo(() => {
    const {
      amazon: externalAmazon = 0,
      azure: externalAzure = 0,
      gcp: externalGcp = 0,
      digitalocean: externalDigitalocean = 0,
      ipv4: externalIpv4 = 0,
      ipv6: externalIpv6 = 0,
      tld: externalDomain = 0,
      domain: externalSubdomain = 0,
      github: externalGithub = 0,
      gitlab: externalGitlab = 0,
      bitbucket: externalBitbucket = 0,
    } = getStatsObject(externalAssetCountsGenericSearchStats || []);

    const {
      amazon: internalAmazon = 0,
      azure: internalAzure = 0,
      gcp: internalGcp = 0,
      digitalocean: internalDigitalocean = 0,
      ipv4: internalIpv4 = 0,
      github: internalGithub = 0,
      gitlab: internalGitlab = 0,
      bitbucket: internalBitbucket = 0,
    } = getStatsObject(internalAssetCountsGenericSearchStats || []);

    const {
      ADDomain = 0,
      ADUser = 0,
      ADComputer = 0,
    } = getStatsObject(assetCountsByTypeStats || []);

    const { webapplication = 0, webseed = 0 } = getStatsObject(
      webappsStats || []
    );

    return {
      cloud: {
        amazon: {
          external: externalAmazon,
          internal: internalAmazon,
        },
        azure: {
          external: externalAzure,
          internal: internalAzure,
        },
        gcp: {
          external: externalGcp,
          internal: internalGcp,
        },
        digitalocean: {
          external: externalDigitalocean,
          internal: internalDigitalocean,
        },
      },
      externalNetwork: {
        ipv4: externalIpv4,
        ipv6: externalIpv6,
        domain: externalDomain,
        subdomain: externalSubdomain,
      },
      applications: {
        webapplication,
        webseed,
      },
      internalNetwork: {
        ipv4: internalIpv4,
        ADDomain,
        ADUser,
        ADComputer,
      },
      code: {
        github: externalGithub + internalGithub,
        gitlab: externalGitlab + internalGitlab,
        bitbucket: externalBitbucket + internalBitbucket,
      },
    } as MonitoringCounts;
  }, [
    externalAssetCountsGenericSearchStats?.length ?? 0,
    internalAssetCountsGenericSearchStats?.length ?? 0,
    assetCountsByTypeStats?.length ?? 0,
    webappsStats?.length ?? 0,
  ]);

  // Aggregate errors
  const error =
    externalError || internalError || assetTypeError || webappsError || null;

  // Combined refetch function
  const refetch = () => {
    refetchExternal();
    refetchInternal();
    refetchAssetType();
    refetchWebapps();
  };

  return {
    data: counts,
    status: useMergeStatus(
      externalAssetCountsGenericSearchStatus,
      internalAssetCountsGenericSearchStatus,
      assetCountsByTypeStatus,
      webappsStatus
    ),
    error,
    refetch,
  };
}
